import {
  Avatar,
  AvatarGroup,
  Collection,
  CollectionItem,
  Pushbutton,
  Pushtext,
  TextEllipsis,
  UiconClipboard,
  UiconKeyHorizontal
} from '@stratumn/atomic';

import classNames from 'classnames';
import {
  ROUTE_EDIT_PROFILE,
  ROUTE_PASSWORD_UPDATE_COMPONENT,
  ROUTE_TEAM
} from 'constant/routes';
import compose from 'lodash.flowright';
import PropType from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import injectSheet from 'react-jss';
import { generatePath, Link, withRouter } from 'react-router-dom';
import addToClipBoard from 'utils/addToClipBoard';
import nameToInitials from 'utils/nameToInitials';
import { withUser } from 'wrappers';

import DecryptAndCopyKeyModal from './decryptKeyModal';
import styles from './myProfile.style';
import RegenerateKeyModal from './regenerateKeyModal';

const MAX_TEAMS = 15;

export function MyProfile(props) {
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [showDecryptModal, setShowDecryptModal] = useState(false);
  const [copiedPublicKey, setClipboardPublicKey] = useState(false);
  const [copiedPrivateKey, setClipboardPrivateKey] = useState(false);
  const [showAllTeams, setShowAllTeams] = useState(false);
  const pkTimeoutRef = useRef(null);
  const skTimeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (pkTimeoutRef.current) {
        clearTimeout(pkTimeoutRef.current);
      }
      if (skTimeoutRef.current) {
        clearTimeout(skTimeoutRef.current);
      }
    },
    []
  );

  // If the current user did not generate any signing key, abort in
  // favor of the "generate key" form
  if (!(props.user && props.user.account && props.user.account.signingKey)) {
    return null;
  }

  const toggleRegenModal = () => setShowRegenModal(!showRegenModal);
  const toggleDecryptModal = async (success = false) => {
    setShowDecryptModal(!showDecryptModal);
    if (success) {
      await setClipboardPrivateKey(true);
      skTimeoutRef.current = setTimeout(() => {
        setClipboardPrivateKey(false);
      }, 1500);
    }
  };

  const {
    classes,
    user: {
      name,
      email,
      phone,
      avatar,
      account: {
        signingKey: { publicKey, privateKey }
      },
      organizations: { nodes: orgs },
      teams: { nodes: teams }
    },
    history
  } = props;

  const displayedTeams = showAllTeams ? teams : teams.slice(0, MAX_TEAMS);

  const copyPublicKey = async key => {
    addToClipBoard(key);
    await setClipboardPublicKey(true);
    pkTimeoutRef.current = setTimeout(() => {
      setClipboardPublicKey(false);
    }, 1500);
  };

  const copyPrivateKey = async key => {
    addToClipBoard(key);
    await setClipboardPrivateKey(true);
    skTimeoutRef.current = setTimeout(() => {
      setClipboardPrivateKey(false);
    }, 1500);
  };

  const onPrivateKeyCopy = async key => {
    const { decrypted, passwordProtected } = key;

    if (passwordProtected) {
      toggleDecryptModal();
      return;
    }
    copyPrivateKey(decrypted);
  };

  const goToUpdatePwdPage = () =>
    props.history.push(ROUTE_PASSWORD_UPDATE_COMPONENT);

  const renderTeamBadge = (team, index) => {
    const org = orgs.find(
      o => String(o.rowId) === String(team.organization.rowId)
    );

    if (org) {
      return (
        <Link
          className={classNames(classes.teamBadge, classes.teamBadgeLink)}
          key={index}
          title={team.name}
          to={generatePath(ROUTE_TEAM, {
            id: team.rowId,
            orgId: org.rowId
          })}
        >
          <AvatarGroup
            initials={nameToInitials(team.name)}
            size={25}
            src={team.avatar}
          />
          <TextEllipsis maxWidth={100} text={team.name} />
        </Link>
      );
    }

    return (
      <div
        className={classNames(classes.teamBadge, classes.teamBadgeNoLink)}
        key={index}
        title={team.name}
      >
        <AvatarGroup
          initials={nameToInitials(team.name)}
          size={25}
          src={team.avatar}
        />
        <TextEllipsis maxWidth={100} text={team.name} />
      </div>
    );
  };

  return (
    <>
      <div className={classes.profileHeaderRow}>
        <Avatar size={56} src={avatar} />
        <Pushbutton onClick={() => history.push(ROUTE_EDIT_PROFILE)} secondary>
          Edit Profile
        </Pushbutton>
      </div>
      <Collection>
        <div className={classes.collectionItemFirstChild}>
          <CollectionItem idle={!name} label="name" value={name} />
        </div>
        <CollectionItem idle={!email} label="email" value={email} />
        <CollectionItem
          idle={!phone}
          label="phone"
          value={phone || 'Not Added'}
        />

        <div className={classes.signingKeyWrapper}>
          <div className={classes.signingKeyHeader}>
            <div className={classes.signingKeyTitleWrapper}>
              <div className={classes.signingKeyTitle}>Account Password</div>
            </div>
          </div>
          <div className={classes.pushTextCustom}>
            <Pushtext
              data-testid="update-pwd-button"
              onClick={goToUpdatePwdPage}
            >
              Update
            </Pushtext>
          </div>
        </div>

        <div className={classes.signingKeyWrapper}>
          <div className={classes.signingKeyHeader}>
            <div className={classes.signingKeyTitleWrapper}>
              <div className={classes.signingKeyTitle}>Signing Keys</div>
              <UiconKeyHorizontal size={27} />
            </div>

            <div className={classes.buttonClipboard}>
              <Pushtext
                data-testid="copy-public-key-button"
                mute={!copiedPublicKey}
                onClick={() => copyPublicKey(publicKey)}
                suffix={<UiconClipboard checked={copiedPublicKey} size={27} />}
              >
                {copiedPublicKey ? 'Copied' : 'Copy'}{' '}
                <span className={classes.bold}>public</span> key
              </Pushtext>
            </div>

            <Pushtext
              data-testid="copy-private-key-button"
              mute={!copiedPrivateKey}
              onClick={() => onPrivateKeyCopy(privateKey)}
              suffix={<UiconClipboard checked={copiedPrivateKey} size={27} />}
            >
              {copiedPrivateKey ? 'Copied' : 'Copy'}{' '}
              <span className={classes.bold}>private</span> key
            </Pushtext>
          </div>
          <div className={classes.pushTextCustom}>
            <Pushtext
              data-testid="regenerate-key-button"
              onClick={toggleRegenModal}
            >
              Regenerate
            </Pushtext>
          </div>
        </div>
        <div className={classes.signingKeyWrapper}>
          <div className={classes.signingKeyHeader}>
            <div className={classes.signingKeyTitleWrapper}>
              <div className={classes.signingKeyTitle}>
                My Teams{' '}
                <span>
                  (If you are not part of the organization you will not be able
                  to access the team)
                </span>
              </div>
            </div>
          </div>
          <div className={classes.teamsList}>
            {displayedTeams.map((team, index) => renderTeamBadge(team, index))}
            {teams.length > MAX_TEAMS && (
              <div className={classes.showMore}>
                <button
                  data-testid="show-more-button"
                  onClick={() => setShowAllTeams(!showAllTeams)}
                >
                  {showAllTeams ? 'Voir moins' : 'Voir plus...'}
                </button>
              </div>
            )}
          </div>
        </div>
      </Collection>

      {showRegenModal && <RegenerateKeyModal toggleModal={toggleRegenModal} />}
      {showDecryptModal && (
        <DecryptAndCopyKeyModal
          encryptedKey={privateKey.decrypted}
          toggleModal={toggleDecryptModal}
        />
      )}
    </>
  );
}

MyProfile.propTypes = {
  classes: PropType.object.isRequired,
  user: PropType.object.isRequired
};

export default compose(withRouter, withUser, injectSheet(styles))(MyProfile);
